/**
 * Recovery and root transfer (docs/e2e.md "Join, PRF, recovery and revocation").
 *
 * - Setup adds an escrow recipient (recovery code or passkey PRF) to the signed directory and
 *   uploads its escrow plus a wrap of every held epoch. The code is shown once and never sent.
 * - Recovery on a new browser: the relay applies the hold/rate-limit policy; the endpoint opens the
 *   escrow locally, pins the escrowed passkeys (authenticated by the code/PRF KEK), verifies the
 *   whole directory chain from genesis, installs commitment-checked epoch keys and then asks the
 *   passkey to add this browser as a recipient. It never bypasses passkey approval.
 */
import {
  createAccountDataKey,
  createDirectoryEscrow,
  createRecoveryCode,
  type DirectoryCheckpoint,
  derivePrfKek,
  deriveRecoveryKek,
  hashCanonical,
  isSessionEndorsement,
  type KeyRecipient,
  openDirectoryEscrow,
  openEscrowEpochKeys,
  passkeyPrfSalt,
  passkeyPrfSaltV2,
  signP256,
  signStatement,
  TrustStore,
  type WebCryptoKey,
  wipeEpochKeys,
  wrapAccountKey,
} from '@viko/crypto'
import {
  RECOVERY_DECISION_DOMAIN,
  RECOVERY_STEP_UP_DOMAIN,
  type RecoveryRequest,
} from '@viko/protocol'
import { verifiedRevocationReport } from './content-enrollment.js'
import { keysApi } from './content-keys-api.js'
import type { BrowserContentSession, KeyContext } from './content-session.js'
import { loadContentTrust, saveContentTrust } from './content-trust.js'
import { loadDevice, type StoredDevice } from './device-key.js'

type Kind = 'recovery' | 'prf'

type Escrow = Awaited<ReturnType<typeof createDirectoryEscrow>>

export async function addEscrow(
  ctx: KeyContext,
  trust: TrustStore,
  device: StoredDevice,
  checkpoint: DirectoryCheckpoint,
  input:
    | { kind: Kind; kek: WebCryptoKey; credentialId?: string; prfSalt?: 2 }
    | { kind: Kind; prepared: Escrow; credentialId?: string },
) {
  const { record, recipient } =
    'prepared' in input
      ? input.prepared
      : await createDirectoryEscrow({
          accountId: ctx.accountId,
          kind: input.kind,
          kek: input.kek,
          ...(input.credentialId ? { credentialId: input.credentialId } : {}),
          ...(input.prfSalt ? { prfSalt: input.prfSalt } : {}),
          credentials: trust.credentials(),
          rpId: trust.rp.rpId,
        })
  if (record.accountId !== ctx.accountId || record.kind !== input.kind)
    throw new Error('Recovery escrow is for another account')
  // A new recovery code supersedes the old one; removing a recipient rotates the epoch.
  const replaced = checkpoint.statement.recipients.filter(
    (r) => r.type === input.kind && (input.kind === 'recovery' || r.id === input.credentialId),
  )
  const recipients: KeyRecipient[] = [
    ...checkpoint.statement.recipients.filter((r) => !replaced.includes(r)),
    recipient,
  ]
  const escrowKey = { recipientId: recipient.id, publicKey: record.publicKey }
  return ctx.vault.withEpochKeyMaterial(ctx.accountId, device.deviceKeyId, async (held) => {
    const rotate = replaced.length > 0
    const epoch = checkpoint.statement.epoch + (rotate ? 1 : 0)
    const current = held.find((k) => k.epoch === checkpoint.statement.epoch)
    if (!current) throw new Error('content locked')
    const raw = rotate ? createAccountDataKey() : current.adk
    try {
      await ctx.publishNext(trust, device, {
        raw,
        recipients,
        epoch,
        escrowKeys: [escrowKey],
      })
      const all = rotate ? [...held, { epoch, adk: raw }] : held
      const wraps = []
      for (const k of all)
        wraps.push({
          epoch: k.epoch,
          wrap: await wrapAccountKey(
            k.adk,
            { version: 1, accountId: ctx.accountId, epoch: k.epoch, recipientId: recipient.id },
            record.publicKey,
          ),
        })
      await keysApi(ctx.api).publishEscrow({ escrow: record, wraps })
      return { recipientId: recipient.id, epochs: all.map((k) => k.epoch) }
    } finally {
      if (rotate) raw.fill(0)
    }
  })
}

export async function unlocked(ctx: KeyContext) {
  const { trust, device } = await ctx.local()
  const directory = await ctx.refresh(trust, device)
  const checkpoint = directory.checkpoint
  if (!checkpoint) throw new Error('Set up encrypted content on this browser first')
  return { trust, device, checkpoint }
}

/** Create a 256-bit recovery code. Show it once; it never leaves this browser. */
export function setupRecoveryCode(session: BrowserContentSession) {
  return session.exclusive(async (ctx) => {
    const { trust, device, checkpoint } = await unlocked(ctx)
    const code = createRecoveryCode()
    await addEscrow(ctx, trust, device, checkpoint, {
      kind: 'recovery',
      kek: await deriveRecoveryKek(code, ctx.accountId),
    })
    return { code }
  })
}

/**
 * Protect keys with a pinned passkey's PRF output (if the authenticator actually returns one).
 * New escrows use the constant v2 salt, so the single-ceremony sign-in (faceid-signin.ts) can open
 * them from the same Face ID that signs in.
 */
export function setupPasskeyPrf(session: BrowserContentSession) {
  return session.exclusive(async (ctx) => {
    const { trust, device, checkpoint } = await unlocked(ctx)
    const v2 = await passkeyPrfSaltV2()
    const salts = trust
      .credentials()
      .filter((c) => !trust.isCredentialRevoked(c.credentialId))
      .map((c) => ({ credentialId: c.credentialId, salt: v2 }))
    const prf = await ctx.passkeys.prf?.(salts, { rpId: trust.rp.rpId })
    if (!prf) return { supported: false as const }
    try {
      const kek = await derivePrfKek(prf.output, ctx.accountId, prf.credentialId)
      await addEscrow(ctx, trust, device, checkpoint, {
        kind: 'prf',
        kek,
        credentialId: prf.credentialId,
        prfSalt: 2,
      })
    } finally {
      prf.output.fill(0)
    }
    return { supported: true as const }
  })
}

export type RecoveryResult =
  | { status: 'held'; request: RecoveryRequest }
  | { status: 'unsupported' }
  | { status: 'recovered'; epochs: number[] }

/**
 * Recover this browser's access with a recovery code or passkey PRF. `endorse` performs the
 * ordinary passkey endorsement of this browser's device key when it has none.
 */
export function recoverAccess(
  session: BrowserContentSession,
  input: { kind: 'recovery'; code: string } | { kind: 'prf' },
  deps: { endorse: () => Promise<StoredDevice>; requestId?: string; origin?: string },
) {
  return session.exclusive(async (ctx): Promise<RecoveryResult> => {
    const relay = keysApi(ctx.api)
    const request = deps.requestId ? { id: deps.requestId } : await relay.startRecovery(input.kind)
    if ('status' in request && request.status === 'held') return { status: 'held', request }
    const bundle = await relay.readRecovery(request.id)
    let kek: WebCryptoKey
    if (input.kind === 'recovery') kek = await deriveRecoveryKek(input.code, ctx.accountId)
    else {
      const credentialId = bundle.escrow.recipientId
      const salt =
        bundle.escrow.prfSalt === 2
          ? await passkeyPrfSaltV2()
          : await passkeyPrfSalt(ctx.accountId, credentialId)
      const prf = await ctx.passkeys.prf?.([{ credentialId, salt }])
      if (!prf || prf.credentialId !== credentialId) return { status: 'unsupported' }
      try {
        kek = await derivePrfKek(prf.output, ctx.accountId, credentialId)
      } finally {
        prf.output.fill(0)
      }
    }
    const opened = await openDirectoryEscrow(bundle.escrow, kek, {
      accountId: ctx.accountId,
      kind: input.kind,
    })
    // Escrowed passkeys are authenticated by the code/PRF KEK: the new browser's trust anchors.
    let trust: TrustStore
    try {
      trust = await loadContentTrust(ctx.accountId)
    } catch {
      trust = new TrustStore({
        rp: { rpId: opened.rpId, origins: [deps.origin ?? location.origin] },
        credentials: [],
      })
    }
    for (const c of opened.credentials)
      if (
        !trust.isCredentialRevoked(c.credentialId) &&
        !trust.credentials().some((x) => x.credentialId === c.credentialId)
      )
        trust.pinCredential(c)
    await saveContentTrust(ctx.accountId, trust)
    let device = await loadDevice()
    if (!device?.endorsement || !(await trust.addEndorsement(device.endorsement)).ok)
      device = await deps.endorse()
    if (!(await trust.addEndorsement(device.endorsement!)).ok)
      throw new Error('This browser needs a passkey endorsement')
    const identity = await ctx.vault.ensureIdentity(ctx.accountId)
    // Verify the entire chain from this browser's own pin (genesis on a new browser).
    const directory = await ctx.refresh(trust, device)
    const checkpoint = directory.checkpoint
    const state = await ctx.vault.state(ctx.accountId)
    if (!checkpoint || !state) throw new Error('Encrypted directory unavailable')
    const keys = await openEscrowEpochKeys(ctx.accountId, opened, bundle.wraps, state.commitments)
    try {
      await ctx.vault.installRecovered(ctx.accountId, device.deviceKeyId, keys)
    } finally {
      wipeEpochKeys(keys)
    }
    // Join: a passkey-approved directory update adds this browser for future epochs. A "Just
    // this session" browser never joins: it keeps the recovered keys in memory only.
    if (
      !isSessionEndorsement(device.endorsement?.statement) &&
      !checkpoint.statement.recipients.some((r) => r.id === device!.deviceKeyId)
    )
      await ctx.vault.withEpochKeyMaterial(ctx.accountId, device.deviceKeyId, async (held) => {
        const current = held.find((k) => k.epoch === checkpoint.statement.epoch)
        if (!current) throw new Error('Recovery escrow is missing the current epoch')
        await ctx.publishNext(trust, device!, {
          raw: current.adk,
          recipients: [
            ...checkpoint.statement.recipients,
            { type: 'device', id: device!.deviceKeyId, publicKey: identity.publicJwk },
          ],
        })
      })
    await relay.completeRecovery(request.id)
    return { status: 'recovered', epochs: keys.map((k) => k.epoch) }
  })
}

/** Approve or deny another browser's pending recovery with this trusted device's key. */
export async function decideRecovery(
  session: BrowserContentSession,
  api: Parameters<typeof keysApi>[0],
  requestId: string,
  decision: 'approve' | 'deny',
) {
  const accountId = await session.accountId()
  const device = await loadDevice()
  if (!device?.endorsement) throw new Error('Only a trusted device can decide')
  if (isSessionEndorsement(device.endorsement.statement))
    throw new Error('Approve recoveries from a trusted device, not a session-only browser')
  const statement = {
    version: 1 as const,
    accountId,
    requestId,
    decision,
    deviceKeyId: device.deviceKeyId,
    issuedAt: Date.now(),
  }
  const signature = await signStatement(
    { sign: (data) => signP256(device.privateKey, data) },
    RECOVERY_DECISION_DOMAIN,
    statement,
  )
  // Approving releases keys to another browser: a fresh passkey tap bound to this exact request.
  const stepUp =
    decision === 'approve'
      ? await session.exclusive(async (ctx) => {
          const { trust } = await ctx.local()
          return ctx.passkeys.assert(await hashCanonical(RECOVERY_STEP_UP_DOMAIN, statement), {
            rpId: trust.rp.rpId,
          })
        })
      : undefined
  return keysApi(api).decideRecovery({ statement, signature, ...(stepUp ? { stepUp } : {}) })
}

/**
 * Alerts for every signed-in device: pending attempts, plus passkey (PRF) escrow releases from the
 * last day. PRF releases have no hold (the one-Face-ID sign-in), so the alert is how a synced-passkey
 * compromise becomes visible; *Lost device* in Devices revokes the passkey.
 */
export async function recoveryAlerts(api: Parameters<typeof keysApi>[0], now = Date.now()) {
  const { requests } = await keysApi(api).listRecovery()
  return requests.filter(
    (r) =>
      ((r.status === 'held' || r.status === 'ready') && r.expiresAt > now) ||
      (r.kind === 'prf' && r.status === 'completed' && now - r.createdAt < 86_400_000),
  )
}

/** unlock-via-mac: browsers a machine unlocked in the last day (audit for every device). */
export async function machineUnlocks(api: Parameters<typeof keysApi>[0], now = Date.now()) {
  const { unlocks } = await keysApi(api).unlockList()
  return unlocks.filter(
    (u) => u.status === 'granted' && now - (u.decidedAt ?? u.createdAt) < 86_400_000,
  )
}

/** Machines' signed revocation state, verified with local enrollment pins (unpinned → unknown). */
export async function machineRevocationState(
  api: Parameters<typeof keysApi>[0],
  accountId: string,
) {
  const { reports } = await keysApi(api).revocationReports()
  const out: { machineId: string; revocationSeq: number; epoch: number; verified: boolean }[] = []
  for (const r of reports) {
    const verified = await verifiedRevocationReport(accountId, r.machineId, r.signed).catch(
      () => undefined,
    )
    out.push({
      machineId: r.machineId,
      revocationSeq: verified?.revocationSeq ?? 0,
      epoch: verified?.epoch ?? 0,
      verified: !!verified,
    })
  }
  return out
}
